import React, {
  createContext,
  useContext,
  ReactNode,
  Children,
  useEffect,
  useState,
  useRef,
} from 'react';
import { Box, useMediaQuery, useTheme, Divider, Stack, IconButton, Tooltip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useUIState } from '../../context/uiState';
import { useToolbar } from '@features/toolbar';
import { hiddenScrollbarStyles, thinScrollbarStyles } from '../../styles/globalStyles';
import { trackEvent } from '../../utils/analytics_utils';

export const SIDEBAR_DOCKED_KEY = 'rightSidebarDocked';

interface LayoutContextType {
  isMobile: boolean;
  hasLeftSidebar: boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

const Layout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const childrenArray = Children.toArray(children);
  const hasLeftSidebar = childrenArray.some(
    child => React.isValidElement(child) && child.type === LayoutWrapper.LeftSidebar,
  );
  const hasTopBar = childrenArray.some(
    child => React.isValidElement(child) && child.type === LayoutWrapper.TopBar,
  );
  const hasBottomBar = childrenArray.some(
    child => React.isValidElement(child) && child.type === LayoutWrapper.BottomBar,
  );

  return (
    <LayoutContext.Provider value={{ isMobile, hasLeftSidebar }}>
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          minWidth: 0,
          flexGrow: 1,
          overflow: 'hidden', // Prevent overall layout from scrolling
        }}
      >
        {hasTopBar && (
          <Box
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0, // Prevent header from shrinking
            }}
          >
            {
              childrenArray.find(
                child => React.isValidElement(child) && child.type === LayoutWrapper.TopBar,
              ) as React.ReactElement | undefined
            }
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 0',
            minHeight: 0,
            minWidth: 0,
            overflow: 'hidden', // Let children handle scrolling as needed
            position: 'relative',
          }}
        >
          {childrenArray.filter(
            child =>
              React.isValidElement(child) &&
              [
                LayoutWrapper.LeftSidebar,
                LayoutWrapper.MainContent,
                LayoutWrapper.RightSidebar,
              ].includes(child.type as any),
          )}
        </Box>
        {hasBottomBar && (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0, // Prevent footer from shrinking
            }}
          >
            {
              childrenArray.find(
                child => React.isValidElement(child) && child.type === LayoutWrapper.BottomBar,
              ) as React.ReactElement | undefined
            }
          </Box>
        )}
      </Box>
    </LayoutContext.Provider>
  );
};

const LeftSidebar: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { state: UIState } = useUIState();
  const { isMobile } = useContext(LayoutContext)!;

  return (
    <>
      <Box
        sx={{
          ...hiddenScrollbarStyles,
          height: '100%',
          width: isMobile ? (UIState.sidebar.isHidden ? '0px' : '250px') : '250px',
          flexShrink: 0,
          flexGrow: 0,
          transition: 'width 0.3s',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
      <Divider orientation="vertical" flexItem />
    </>
  );
};

const MainContent: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    id="main-content"
    sx={{
      ...thinScrollbarStyles,
      height: '100%',
      flex: '1 1 0',
      minHeight: 0,
      minWidth: 0,
      overflowX: 'auto',
      overflowY: 'auto',
      display: 'flex',
      justifyContent: 'center',
    }}
  >
    <Box
      sx={{
        minWidth: 0,
        maxWidth: '1500px',
        width: '100%',
        mx: 'auto',
        px: 2, // Add some horizontal padding
      }}
    >
      {children}
    </Box>
  </Box>
);

const RightSidebar: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isMobile } = useContext(LayoutContext)!;
  const [hasVisibleContent, setHasVisibleContent] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [isDocked, setIsDocked] = useState(() => {
    const storedValue = localStorage.getItem(SIDEBAR_DOCKED_KEY);
    return storedValue ? JSON.parse(storedValue) : false;
  });
  const stackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkVisibleContent = () => {
      if (stackRef.current) {
        const childElements = stackRef.current.children;
        let visibleCount = 0;

        for (let i = 0; i < childElements.length; i++) {
          const child = childElements[i] as HTMLElement;
          const dataVisible = child.getAttribute('data-visible');
          if (dataVisible !== 'false') {
            visibleCount++;
          }
        }

        setHasVisibleContent(visibleCount > 0);
      }
    };

    // Check content visibility after a short delay to ensure render is complete
    const timer = setTimeout(() => {
      checkVisibleContent();
      setIsInitialRender(false);
    }, 10);

    // Set up a MutationObserver to watch for changes in the sidebar's children
    const observer = new MutationObserver(checkVisibleContent);
    if (stackRef.current) {
      observer.observe(stackRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-visible'],
      });
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [children]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_DOCKED_KEY, JSON.stringify(isDocked));
  }, [isDocked]);

  const toggleDock = () => {
    setIsDocked((prev: boolean) => !prev);
    trackEvent({
      event: 'sidebar_dock_toggle',
      is_docked: !isDocked,
    });
  };

  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        width: hasVisibleContent ? (isDocked ? '30px' : '300px') : '0px',
        flexShrink: 0,
        flexGrow: 0,
        transition: 'width 0.3s ease-in-out',
        overflow: 'hidden',
        visibility: isInitialRender ? 'hidden' : 'visible',
        opacity: isInitialRender ? 0 : 1,
        position: 'relative',
        height: '100%',
      }}
    >
      {isDocked ? (
        <Box
          onClick={toggleDock}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderLeft: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChevronLeftIcon />
        </Box>
      ) : (
        <Tooltip title="Dock sidebar" placement="left">
          <IconButton
            onClick={toggleDock}
            sx={{
              position: 'absolute',
              top: '-5px',
              right: '10px',
              zIndex: 1,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
      )}
      <Box
        sx={{
          width: '300px',
          transform: isDocked ? 'translateX(260px)' : 'translateX(0)',
          transition: 'transform 0.3s ease-in-out',
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Stack
          sx={{
            my: 4,
            mr: 2,
            ml: isDocked ? 0 : 2,
          }}
          spacing={2}
          ref={stackRef}
        >
          {children}
        </Stack>
      </Box>
    </Box>
  );
};

const BottomBar: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      ...hiddenScrollbarStyles,
      minHeight: '60px',
      minWidth: 0,
      overflowX: 'auto',
      overflowY: 'hidden',
      display: 'flex',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const TopBar: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      ...hiddenScrollbarStyles,
      minHeight: '50px',
      minWidth: 0,
      overflowX: 'auto',
      overflowY: 'hidden',
      display: 'flex',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const LayoutWrapper: React.FC<{ children: ReactNode }> & {
  LeftSidebar: typeof LeftSidebar;
  MainContent: typeof MainContent;
  RightSidebar: typeof RightSidebar;
  BottomBar: typeof BottomBar;
  TopBar: typeof TopBar;
} = ({ children }) => {
  const { setScrollContainer } = useToolbar();
  const childrenArray = Children.toArray(children);
  const hasMainContent = childrenArray.some(
    child => React.isValidElement(child) && child.type === LayoutWrapper.MainContent,
  );

  useEffect(() => {
    setScrollContainer('#main-content');
    return () => setScrollContainer(undefined);
  }, [setScrollContainer]);

  if (!hasMainContent) {
    throw new Error('LayoutWrapper must include a MainContent component');
  }

  return <Layout>{children}</Layout>;
};

LayoutWrapper.LeftSidebar = LeftSidebar;
LayoutWrapper.MainContent = MainContent;
LayoutWrapper.RightSidebar = RightSidebar;
LayoutWrapper.BottomBar = BottomBar;
LayoutWrapper.TopBar = TopBar;

export default LayoutWrapper;
